import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';

const APP_ORIGIN = String(import.meta.env.VITE_INEDITA_APP_ORIGIN || 'https://inedito.schlapp.com.br').replace(/\/$/, '');
const visualRoot = '/visuals/biomorphic';
const journeyVideoSrc = String(import.meta.env.VITE_INEDITA_JOURNEY_VIDEO_SRC || '').trim();
const journeyPosterSrc = String(import.meta.env.VITE_INEDITA_JOURNEY_POSTER_SRC || '').trim();

const image = (name) => `${visualRoot}/${name}.webp`;
const appPath = (path) => `${APP_ORIGIN}${path}`;
const startHref = appPath('/entrar?next=%2Fapp');
const checkoutHref = appPath('/entrar?next=%2Fcheckout');
const applyHref = appPath('/entrar?next=%2Fapoio');

const bioAssets = {
  hero: { src: image('BIOMORPHIC-01-novo-gargalo'), alt: 'Estrutura biomórfica transparente envolve um núcleo luminoso entre formas escuras.' },
  vocabulary: { src: image('BIOMORPHIC-03-mergulho'), alt: 'Camadas translúcidas e linhas de luz se aprofundam em uma estrutura escura, evocando uma investigação em andamento.' },
  method: { src: image('BIOMORPHIC-04-cartografia'), alt: 'Fragmentos luminosos se conectam por linhas finas até formar uma configuração única sobre um campo escuro.' },
  questions: { src: image('BIOMORPHIC-02-perguntas'), alt: 'Instrumentos ópticos luminosos aparecem dentro de uma forma biomórfica escura, sugerindo investigação cuidadosa.' },
  extraction: { src: image('BIOMORPHIC-05-extracao'), alt: 'Um núcleo verde-azulado se destaca dentro de uma estrutura orgânica escura, como um sinal extraído de muitos fragmentos.' },
  council: { src: image('BIOMORPHIC-06-conselho'), alt: 'Lentes e estruturas circulares examinam um núcleo luminoso sob diferentes ângulos em um ambiente escuro.' },
  reality: { src: image('BIOMORPHIC-07-realidade'), alt: 'Um núcleo luminoso atravessa formas de pressão e equilíbrio, sugerindo uma hipótese confrontada com a realidade.' },
  reveal: { src: image('BIOMORPHIC-08-revelacao'), alt: 'Uma forma luminosa é revelada dentro de uma estrutura biomórfica, sugerindo uma combinação pessoal compreendida.' },
  execution: { src: image('BIOMORPHIC-09-execucao'), alt: 'Um núcleo central se desdobra em caminhos organizados dentro de uma estrutura escura, sugerindo opções de execução.' },
  human: { src: image('BIOMORPHIC-10-estrategia-humana'), alt: 'Uma passagem entre instrumento de precisão e dossiê humano representa a transição para uma decisão acompanhada.' },
};

const creationWords = ['sites', 'aplicativos', 'vídeos', 'marcas', 'automações', 'conteúdo', 'negócios'];
const HERO_TAIL = 'saber o que vale a pena criar.';

const publicOffers = [
  { price: 'R$297', name: 'INÉDITA - DESCOBERTA', copy: 'Descubra o que você poderia criar.', cta: 'Descobrir minhas oportunidades' },
  { price: 'R$997', name: 'INÉDITA - EXECUÇÃO', copy: 'Não descubra apenas o que fazer. Descubra como fazer.', cta: 'Quero a ideia + plano' },
  { price: 'R$1.497', name: 'INÉDITA - ESTRATÉGIA', copy: 'Chegue à conversa com a investigação e o plano já estruturados.', cta: 'Quero o Método + sessão estratégica' },
];

function BrandLockup({ href = '/', className = '' }) {
  return <a className={`lab-brand ${className}`.trim()} href={href} aria-label="INÉDITA Labs, página inicial"><span className="lab-brand__mark" aria-hidden="true"><img src="/brand/inedito-gota-semente-alpha.webp" alt="" /></span><span>INÉDITA <small>LABS</small></span></a>;
}

function Header({ variant = 'dark', label = 'Landing Lab', publicSurface = false }) {
  return (
    <header className={`lab-header lab-header--${variant} ${publicSurface ? 'lab-header--public' : ''}`}>
      <BrandLockup href={publicSurface ? '/biomorphic' : '/'} />
      {!publicSurface ? <span className="lab-header__label">{label}</span> : null}
      {!publicSurface ? <nav className="lab-nav" aria-label="Variações da landing page"><a href="/biomorphic">Biomorphic</a><a href="/business">Empresas</a><a href="/journey">Jornada</a></nav> : null}
      <a className="text-link" href={startHref}>Começar</a>
    </header>
  );
}

function Arrow() {
  return <span className="arrow" aria-hidden="true"><svg viewBox="0 0 16 16" focusable="false"><path d="M3 13 13 3M5 3h8v8" /></svg></span>;
}

function CTA({ href, children, tone = 'dark' }) {
  return <a className={`cta cta--${tone}`} href={href}>{children}<Arrow /></a>;
}

function Figure({ src, alt, className = '', priority = false }) {
  return <figure className={`image-figure ${className}`.trim()}><img src={src} alt={alt} loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'auto'} decoding="async" /><span aria-hidden="true" /></figure>;
}

function Footer({ variant = 'dark' }) {
  return <footer className={`lab-footer lab-footer--${variant}`}><BrandLockup /><span>Observar. Decidir. Agir. Medir. Aprender. Adaptar.</span><a href={applyHref}>Acompanhamento por aplicação <Arrow /></a></footer>;
}

function useReducedMotionPreference() {
  const [reducedMotion, setReducedMotion] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const change = (event) => setReducedMotion(event.matches);
    media.addEventListener?.('change', change);
    return () => media.removeEventListener?.('change', change);
  }, []);
  return reducedMotion;
}

function useHeroTail(reducedMotion) {
  const [tail, setTail] = useState(HERO_TAIL);
  useEffect(() => {
    if (reducedMotion) {
      setTail(HERO_TAIL);
      return undefined;
    }
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let interval = 0;
    const reveal = (progress) => {
      const locked = Math.floor(progress * HERO_TAIL.length);
      setTail(HERO_TAIL.split('').map((character, index) => {
        if (character === ' ' || index < locked) return character;
        return alphabet[(index * 13 + Math.floor(progress * 120)) % alphabet.length];
      }).join(''));
    };
    const start = window.setTimeout(() => {
      let step = 0;
      interval = window.setInterval(() => {
        step += 1;
        const progress = Math.min(1, step / 16);
        reveal(progress);
        if (progress === 1) {
          window.clearInterval(interval);
          setTail(HERO_TAIL);
        }
      }, 46);
    }, 260);
    const failSafe = window.setTimeout(() => {
      if (interval) window.clearInterval(interval);
      setTail(HERO_TAIL);
    }, 1450);
    return () => { window.clearTimeout(start); window.clearTimeout(failSafe); if (interval) window.clearInterval(interval); };
  }, [reducedMotion]);
  return tail;
}

function useBiomorphicStageMotion(rootRef, reducedMotion) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const stages = [...root.querySelectorAll('[data-bio-stage]')];
    if (reducedMotion || !('IntersectionObserver' in window)) {
      stages.forEach((stage) => stage.classList.add('is-visible'));
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) entry.target.classList.add('is-visible'); });
    }, { threshold: 0.18 });
    stages.forEach((stage) => observer.observe(stage));
    return () => observer.disconnect();
  }, [rootRef, reducedMotion]);
}

function useCreationVocabulary(reducedMotion) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const words = [...node.querySelectorAll('[data-vocabulary-word]')];
    if (reducedMotion) {
      node.classList.remove('is-scroll-controlled');
      words.forEach((word) => word.classList.add('is-active'));
      return undefined;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      const stage = node.closest('[data-bio-stage]') || node;
      const rect = stage.getBoundingClientRect();
      const travel = Math.max(1, stage.offsetHeight - window.innerHeight);
      const progress = Math.min(1, Math.max(0, -rect.top / travel));
      const isInReadingRange = rect.top < window.innerHeight && rect.bottom > 0;
      const activeCount = isInReadingRange ? Math.max(1, Math.ceil(progress * creationWords.length)) : 0;
      words.forEach((word, index) => {
        word.classList.toggle('is-active', index < activeCount);
        word.classList.toggle('is-current', index === activeCount - 1);
        word.classList.toggle('is-pending', index >= activeCount);
      });
      node.classList.add('is-scroll-controlled');
    };
    const schedule = () => {
      if (frame) window.cancelAnimationFrame(frame);
      update();
      frame = window.requestAnimationFrame(update);
    };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => { window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule); if (frame) window.cancelAnimationFrame(frame); };
  }, [reducedMotion]);
  return ref;
}

function StageBackdrop({ visual, priority = false }) {
  return <div className="bio-stage__scene" aria-hidden="true"><img src={visual.src} alt="" loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'auto'} decoding="async" /><span className="bio-stage__veil" /><svg className="bio-stage__threads" viewBox="0 0 1000 700" preserveAspectRatio="none"><path d="M-20 562 C185 428 304 616 488 418 S761 238 1020 160" /><path d="M-20 630 C192 488 345 662 576 520 S803 338 1020 292" /></svg><span className="bio-stage__signal" /></div>;
}

function BioStage({ id, visual, className = '', priority = false, children }) {
  return <section id={id} className={`bio-stage ${className}`.trim()} data-bio-stage><StageBackdrop visual={visual} priority={priority} /><div className="bio-stage__content">{children}</div></section>;
}

function PublicLadder({ tone = 'dark', visual = null }) {
  return (
    <section className={`offer-ladder offer-ladder--${tone} ${visual ? 'offer-ladder--visual' : ''}`} aria-labelledby="offer-title" data-bio-stage={visual ? '' : undefined}>
      {visual ? <StageBackdrop visual={visual} /> : null}
      <div className="section-lead"><p className="section-kicker">Escolha a profundidade</p><h2 id="offer-title">A descoberta pode virar caminho.</h2><p>Os três caminhos públicos aumentam a profundidade da decisão. O acompanhamento mais próximo acontece por aplicação.</p></div>
      <div className="offer-list">
        {publicOffers.map((offer, index) => <article className={`offer-row ${index === 1 ? 'offer-row--active' : ''}`} key={offer.price}>
          <span className="offer-row__index">0{index + 1}</span><div><h3>{offer.name}</h3><p>{offer.copy}</p></div><strong>{offer.price}</strong><a href={checkoutHref}>{offer.cta}<Arrow /></a>
        </article>)}
      </div>
    </section>
  );
}

function BiomorphicPage() {
  const pageRef = useRef(null);
  const reducedMotion = useReducedMotionPreference();
  const heroTail = useHeroTail(reducedMotion);
  const vocabularyRef = useCreationVocabulary(reducedMotion);
  useBiomorphicStageMotion(pageRef, reducedMotion);
  return <main ref={pageRef} className="page page--biomorphic">
    <Header variant="dark" publicSurface />
    <BioStage id="biomorphic-hero" className="bio-hero" visual={bioAssets.hero} priority><div className="bio-hero__copy"><h1 aria-label={`Se todos podem criar praticamente qualquer coisa, o verdadeiro gargalo passa a ser ${HERO_TAIL}`}><span>Se todos podem criar praticamente qualquer coisa,</span><span>o verdadeiro gargalo passa a ser</span><strong aria-hidden="true">{heroTail}</strong></h1><p>Não falta ferramenta. Falta reconhecer o que merece existir antes de gastar energia construindo.</p><a className="bio-scroll-cue" href="#novo-gargalo">Role para investigar <Arrow /></a></div></BioStage>

    <BioStage id="novo-gargalo" className="bio-vocabulary-stage" visual={bioAssets.vocabulary}><div className="bio-vocabulary-stage__layout"><div><p className="bio-statement">Em um mundo onde praticamente qualquer pessoa pode usar IA para criar...</p><div ref={vocabularyRef} className="bio-vocabulary" aria-label={creationWords.join(', ')}>{creationWords.map((word) => <span className="is-active" data-vocabulary-word key={word}>{word}</span>)}</div></div><div className="bio-turn"><p>Se criar deixou de ser o problema...</p><h2>onde está o novo gargalo?</h2><strong>Na decisão sobre o que merece existir.</strong></div></div></BioStage>

    <BioStage id="metodo" className="bio-method" visual={bioAssets.method}><div className="bio-copy"><p className="bio-label">INÉDITA LABS APRESENTA</p><h2>A oportunidade que só faz sentido para você.</h2><p>Existe uma oportunidade escondida na combinação do que você sabe, viveu, possui e consegue acessar.</p><strong>O Método INÉDITA foi criado para encontrá-la.</strong><CTA href={startHref}>Descobrir minhas oportunidades</CTA></div></BioStage>

    <BioStage id="investigacao" className="bio-questions" visual={bioAssets.questions}><div className="bio-copy"><h2>Talvez você não precise de melhores respostas.</h2><p className="bio-questions__answer">Talvez precise de perguntas que nunca fizeram para você.</p><p>Uma investigação premium acompanha o sinal e aumenta a resolução exatamente onde ainda falta evidência.</p><a className="inline-link inline-link--light" href={startHref}>Começar minha investigação <Arrow /></a></div></BioStage>

    <BioStage id="extracao" className="bio-extraction" visual={bioAssets.extraction}><div className="bio-copy"><h2>O que parecia comum começa a revelar valor.</h2><p>Conhecimento subestimado, problemas monetizáveis, acessos pouco explorados e restrições reais podem formar uma combinação difícil de reproduzir em outra pessoa.</p><blockquote>Não procuramos apenas aquilo em que você é bom. Procuramos combinações que sejam difíceis de reproduzir.</blockquote></div></BioStage>

    <BioStage id="conselho" className="bio-council" visual={bioAssets.council}><div className="bio-copy"><h2>Uma ideia não chega até você só porque parece boa.</h2><p>Antes de aparecer, uma possibilidade atravessa olhares que confrontam potencial, aderência, fragilidade, modelo de negócio e realidade.</p><strong>Antes de chegar até você, sua ideia precisa sobreviver ao Conselho.</strong></div></BioStage>

    <BioStage id="realidade" className="bio-reality" visual={bioAssets.reality}><div className="bio-copy"><h2>Porque uma ideia fascinante ainda pode ser um péssimo negócio.</h2><p>Quem pagaria? Por que agora? Como a pessoa seria alcançada? É possível validar antes de construir tudo?</p><strong>Ideias interessantes não são automaticamente oportunidades que merecem ser exploradas.</strong></div></BioStage>

    <BioStage id="revelacao" className="bio-reveal" visual={bioAssets.reveal}><div className="bio-copy"><p className="bio-label">A REVELAÇÃO</p><h2>Encontramos uma assimetria.</h2><p>Você entende por que aquela possibilidade apareceu especificamente para você, o que a sustenta e o que ainda precisa ser provado.</p><CTA href={startHref}>Descobrir minhas oportunidades</CTA></div></BioStage>

    <PublicLadder tone="dark" visual={bioAssets.execution} />
    <BioStage id="acompanhamento" className="bio-application" visual={bioAssets.human}><div className="bio-copy"><h2>Algumas ideias pedem mais do que um plano.</h2><p>Para determinados projetos, a INÉDITA Labs oferece formatos de acompanhamento mais próximos, sujeitos à análise do projeto e disponibilidade.</p></div><CTA href={applyHref} tone="light">Aplicar para acompanhamento</CTA></BioStage>
    <Footer variant="dark" />
  </main>;
}

const businessSignals = [
  { title: 'Capacidades internas', copy: 'O que a equipe já sabe fazer e ainda pode combinar de outro modo.' },
  { title: 'Ativos subutilizados', copy: 'Recursos, conhecimento e acessos que ainda não encontraram um veículo.' },
  { title: 'Problemas recorrentes', copy: 'Fricções reais que podem apontar para uma nova linha de produto ou serviço.' },
  { title: 'Próxima decisão', copy: 'Uma hipótese legível o bastante para investigar, testar e priorizar.' },
];

const seededSignal = (index, offset = 0) => {
  const value = Math.sin((index + 1) * 12.9898 + (offset + 1) * 78.233) * 43758.5453;
  return value - Math.floor(value);
};

const signalFieldParticles = Array.from({ length: 128 }, (_, index) => {
  const radial = Math.sqrt((index + 0.5) / 128);
  const angle = index * 2.399963229728653 + seededSignal(index, 1) * 0.38;
  const turbulence = seededSignal(index, 2) - 0.5;
  return {
    id: index,
    x: Math.cos(angle) * radial * 1.22 + turbulence * 0.24 + 0.08,
    y: Math.sin(angle) * radial * 0.82 + (seededSignal(index, 3) - 0.5) * 0.2 - 0.02,
    z: (seededSignal(index, 4) * 2 - 1) * 1.12 + (1 - radial) * 0.24,
    size: 0.72 + seededSignal(index, 5) * 1.75,
    phase: seededSignal(index, 6) * Math.PI * 2,
    tone: index === 19 || index === 61 ? 'focus' : index % 4 === 0 ? 'signal' : 'quiet',
  };
});

const signalFieldLinks = signalFieldParticles
  .filter(({ id }) => id % 4 === 0)
  .map((particle) => {
    const nearest = signalFieldParticles.reduce((candidate, possible) => {
      if (possible.id === particle.id) return candidate;
      const candidateDistance = (candidate.x - particle.x) ** 2 + (candidate.y - particle.y) ** 2 + (candidate.z - particle.z) ** 2;
      const possibleDistance = (possible.x - particle.x) ** 2 + (possible.y - particle.y) ** 2 + (possible.z - particle.z) ** 2;
      return possibleDistance < candidateDistance ? possible : candidate;
    }, signalFieldParticles[particle.id === 0 ? 1 : 0]);
    return [particle.id, nearest.id];
  });

function SignalField3D() {
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const reducedMotion = useReducedMotionPreference();

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d', { alpha: true });
    if (!host || !canvas || !context) return undefined;

    let width = 0;
    let height = 0;
    let frame = 0;
    let isIntersecting = true;
    let deviceRatio = 1;
    const pointer = { x: 0, y: 0 };

    const rotate = (particle, yaw, pitch) => {
      const cosineYaw = Math.cos(yaw);
      const sineYaw = Math.sin(yaw);
      const x = particle.x * cosineYaw - particle.z * sineYaw;
      const z = particle.x * sineYaw + particle.z * cosineYaw;
      const cosinePitch = Math.cos(pitch);
      const sinePitch = Math.sin(pitch);
      return {
        x,
        y: particle.y * cosinePitch - z * sinePitch,
        z: particle.y * sinePitch + z * cosinePitch,
      };
    };

    const drawTriangle = (point, time) => {
      const { particle, x, y, depth, scale } = point;
      const alpha = Math.min(.98, .22 + ((depth + 1.5) / 3.1) * .72);
      const color = particle.tone === 'focus' ? '197, 168, 115' : particle.tone === 'signal' ? '134, 179, 155' : '237, 233, 223';
      const size = Math.max(1.25, particle.size * scale * (1.15 + ((depth + 1.5) / 3.1) * 1.85));
      context.save();
      context.translate(x, y);
      context.rotate(particle.phase + time * .00022 + depth * .48);
      context.globalCompositeOperation = 'lighter';
      context.globalAlpha = alpha;
      context.beginPath();
      context.moveTo(0, -size);
      context.lineTo(size * .88, size * .74);
      context.lineTo(-size * .88, size * .74);
      context.closePath();
      context.fillStyle = 'rgb(' + color + ')';
      context.fill();
      if (particle.tone === 'focus') {
        context.globalAlpha = .82;
        context.lineWidth = .75;
        context.strokeStyle = 'rgba(237, 233, 223, .92)';
        context.stroke();
      }
      context.restore();
    };

    const draw = (time = 0) => {
      if (!width || !height) return;
      context.clearRect(0, 0, width, height);
      const activeParticles = signalFieldParticles.slice(0, width < 540 ? 76 : signalFieldParticles.length);
      const yaw = .24 + time * .000105 + pointer.x * .34;
      const pitch = -.11 + Math.sin(time * .000074) * .075 + pointer.y * .24;
      const distance = 3.35;
      const scale = Math.min(width, height) / 520;
      const projected = activeParticles.map((particle) => {
        const rotated = rotate(particle, yaw, pitch);
        const perspective = distance / (distance - rotated.z);
        return {
          particle,
          x: width * .53 + rotated.x * width * .34 * perspective,
          y: height * .5 + rotated.y * height * .42 * perspective,
          depth: rotated.z,
          scale: perspective * scale,
        };
      }).sort((a, b) => a.depth - b.depth);
      const points = new Map(projected.map((point) => [point.particle.id, point]));

      context.save();
      signalFieldLinks.forEach(([from, to], index) => {
        const fromPoint = points.get(from);
        const toPoint = points.get(to);
        if (!fromPoint || !toPoint) return;
        const depth = (fromPoint.depth + toPoint.depth) / 2;
        context.globalAlpha = Math.max(.06, .11 + ((depth + 1.5) / 3.1) * .17);
        context.strokeStyle = index % 3 === 0 ? 'rgba(197, 168, 115, .72)' : 'rgba(134, 179, 155, .72)';
        context.lineWidth = Math.max(.45, .72 * scale);
        context.setLineDash(index % 3 === 0 ? [2.5, 5.5] : [1.5, 6]);
        context.beginPath();
        context.moveTo(fromPoint.x, fromPoint.y);
        context.lineTo(toPoint.x, toPoint.y);
        context.stroke();
      });
      context.restore();
      context.setLineDash([]);
      projected.forEach((point) => drawTriangle(point, time));
    };

    const stop = () => {
      if (!frame) return;
      window.cancelAnimationFrame(frame);
      frame = 0;
    };

    const render = (time) => {
      frame = 0;
      draw(time);
      if (isIntersecting && !document.hidden && !reducedMotion) frame = window.requestAnimationFrame(render);
    };

    const start = () => {
      if (!isIntersecting || document.hidden) return;
      if (reducedMotion) {
        draw(0);
        return;
      }
      if (!frame) frame = window.requestAnimationFrame(render);
    };

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = Math.max(1, Math.round(bounds.width));
      height = Math.max(1, Math.round(bounds.height));
      deviceRatio = Math.min(window.devicePixelRatio || 1, 1.6);
      canvas.width = Math.round(width * deviceRatio);
      canvas.height = Math.round(height * deviceRatio);
      context.setTransform(deviceRatio, 0, 0, deviceRatio, 0, 0);
      draw(0);
    };

    const onPointerMove = (event) => {
      if (reducedMotion) return;
      const bounds = host.getBoundingClientRect();
      pointer.x = ((event.clientX - bounds.left) / bounds.width - .5) * 2;
      pointer.y = ((event.clientY - bounds.top) / bounds.height - .5) * 2;
    };
    const resetPointer = () => { pointer.x = 0; pointer.y = 0; };
    const onVisibilityChange = () => { if (document.hidden) stop(); else start(); };
    const intersectionObserver = 'IntersectionObserver' in window ? new IntersectionObserver(([entry]) => {
      isIntersecting = entry.isIntersecting;
      if (isIntersecting) start(); else stop();
    }, { threshold: .12 }) : null;
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;

    resize();
    intersectionObserver?.observe(host);
    resizeObserver?.observe(canvas);
    if (!resizeObserver) window.addEventListener('resize', resize);
    host.addEventListener('pointermove', onPointerMove);
    host.addEventListener('pointerleave', resetPointer);
    document.addEventListener('visibilitychange', onVisibilityChange);
    start();

    return () => {
      stop();
      intersectionObserver?.disconnect();
      resizeObserver?.disconnect();
      if (!resizeObserver) window.removeEventListener('resize', resize);
      host.removeEventListener('pointermove', onPointerMove);
      host.removeEventListener('pointerleave', resetPointer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [reducedMotion]);

  return <div ref={hostRef} className="business-constellation business-constellation--3d" role="img" aria-label="Campo tridimensional de sinais da empresa. Partículas triangulares em profundidade se relacionam até revelar uma direção a investigar.">
    <canvas ref={canvasRef} className="business-constellation__canvas" aria-hidden="true" />
    <span className="business-constellation__caption">Sinais reais. Relações possíveis.</span>
    <span className="business-constellation__focus" aria-hidden="true">uma direção<br />a investigar</span>
  </div>;
}

function BusinessOfferLadder() {
  return <section className="business-offers" aria-labelledby="business-offers-title">
    <div className="business-offers__lead"><h2 id="business-offers-title">A descoberta ganha a profundidade que a próxima decisão pede.</h2><p>Os três caminhos públicos aumentam a resolução da investigação. O acompanhamento mais próximo acontece por aplicação.</p></div>
    <div className="business-offers__list">{publicOffers.map((offer) => <article className="business-offer" key={offer.price}><strong>{offer.price}</strong><div><h3>{offer.name}</h3><p>{offer.copy}</p></div><a href={checkoutHref}>{offer.cta}<Arrow /></a></article>)}</div>
  </section>;
}

function BusinessPage() {
  return <main className="page page--business">
    <Header variant="dark" label="LP2 / Empresas" />
    <section className="business-hero" aria-labelledby="business-title"><div className="business-hero__copy"><h1 id="business-title">Uma oportunidade pode já estar na sua empresa.</h1><p>Capacidades, ativos, acessos e problemas reais deixam pistas. O Método INÉDITA organiza esses sinais antes que uma direção vire investimento.</p><CTA href={startHref} tone="brass">Mapear uma oportunidade para minha empresa</CTA><small>Você verá o caminho adequado ao nível de profundidade necessário.</small></div><SignalField3D /></section>

    <section className="business-listening" aria-labelledby="business-listening-title"><div className="business-listening__lead"><h2 id="business-listening-title">A empresa já produz mais sinais do que consegue enxergar.</h2><p>Parte deles está em reuniões, rotinas, conversas com clientes e decisões que parecem isoladas. A investigação começa ao aproximar o que costuma ficar disperso.</p></div><div className="business-signal-list">{businessSignals.map((signal) => <article key={signal.title}><h3>{signal.title}</h3><p>{signal.copy}</p></article>)}</div></section>

    <section className="business-method" aria-labelledby="business-method-title"><div className="business-method__copy"><h2 id="business-method-title">O que parece disperso pode revelar uma direção.</h2><p>O método parte da realidade da empresa, encontra relações que merecem atenção e transforma uma possibilidade em hipótese de trabalho.</p><ol className="business-method__steps"><li>Mapear a realidade</li><li>Encontrar assimetrias</li><li>Formular oportunidades</li><li>Selecionar o próximo experimento</li></ol></div><aside className="business-method__aside"><span>Uma hipótese não elimina a incerteza.</span><strong>Ela mostra o que vale ser compreendido antes de avançar.</strong></aside></section>

    <section className="business-pressure" aria-labelledby="business-pressure-title"><div><h2 id="business-pressure-title">Uma oportunidade não avança só porque parece boa.</h2><p>A hipótese é confrontada com o que sustenta uma decisão responsável. Sem promessas automáticas, o foco é tornar a próxima conversa mais específica.</p></div><ul className="business-pressure__questions"><li>Qual problema precisa de mais evidência?</li><li>Quem poderia reconhecer valor nessa proposta?</li><li>Por qual canal essa conversa poderia começar?</li><li>Qual é o menor próximo teste que faz sentido?</li></ul><a className="inline-link inline-link--brass" href={startHref}>Começar pelo mapa da oportunidade <Arrow /></a></section>

    <BusinessOfferLadder />
    <section className="business-application"><div><h2>Alguns projetos avançam por aplicação.</h2><p>Se a oportunidade exigir maior participação humana, análise de contexto ou execução acompanhada, o próximo passo é uma aplicação.</p></div><CTA href={applyHref} tone="brass">Aplicar para acompanhamento</CTA></section>
    <Footer variant="dark" />
  </main>;
}

const journeyChapters = [
  ['01', 'O sinal aparece', 'Uma oportunidade começa como fragmento.'],
  ['02', 'A pergunta muda o mapa', 'A história deixa de ser contexto e passa a ser evidência.'],
  ['03', 'As conexões ganham forma', 'Capacidades, acessos e problemas se aproximam.'],
  ['04', 'A hipótese é pressionada', 'O que parece fascinante precisa encontrar a realidade.'],
  ['05', 'Uma direção se destaca', 'Poucas possibilidades chegam com razão suficiente para continuar.'],
  ['06', 'A decisão começa', 'Você escolhe até onde levar a descoberta.'],
];

function useScrollVideo(stageRef, videoRef, reducedMotion) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (reducedMotion) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const stage = stageRef.current;
      const video = videoRef.current;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const range = Math.max(1, stage.offsetHeight - window.innerHeight);
      const next = Math.min(1, Math.max(0, -rect.top / range));
      setProgress(next);
      if (video?.readyState >= 1 && Number.isFinite(video.duration) && video.duration > 0) {
        const target = Math.min(video.duration - 0.05, Math.max(0, next * video.duration));
        if (Math.abs(video.currentTime - target) > 0.035) video.currentTime = target;
      }
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    videoRef.current?.addEventListener('loadedmetadata', schedule);
    return () => { window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule); videoRef.current?.removeEventListener('loadedmetadata', schedule); if (frame) window.cancelAnimationFrame(frame); };
  }, [stageRef, videoRef, reducedMotion]);
  return progress;
}

function JourneyPage() {
  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const progress = useScrollVideo(stageRef, videoRef, reducedMotion);
  const active = Math.min(journeyChapters.length - 1, Math.floor(progress * journeyChapters.length));
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const change = (event) => setReducedMotion(event.matches);
    media.addEventListener?.('change', change);
    return () => media.removeEventListener?.('change', change);
  }, []);
  return <main className="page page--journey">
    <Header variant="dark" label="Variação 03 / Jornada" />
    <section className="scrub-stage" ref={stageRef} aria-labelledby="scrub-title">
      <div className="scrub-sticky"><div className="scrub-media" aria-hidden="true"><video ref={videoRef} poster={journeyPosterSrc || undefined} muted playsInline preload={reducedMotion || !journeyVideoSrc ? 'none' : 'metadata'}>{!reducedMotion && journeyVideoSrc ? <source src={journeyVideoSrc} type="video/mp4" /> : null}</video><span /></div><div className="scrub-copy"><p className="section-kicker">Uma jornada para quem ainda não tem uma ideia</p><h1 id="scrub-title">Você não precisa começar com uma ideia.</h1><p>Comece entendendo o que já existe em você e ao seu redor.</p><CTA href={startHref}>Começar a investigação</CTA></div><div className="scrub-progress"><span style={{ transform: `scaleX(${progress})` }} /></div><div className="scrub-chapters" aria-live="polite">{journeyChapters.map(([number, title, copy], index) => <article className={index === active ? 'is-active' : ''} key={number}><span>{number}</span><div><h2>{title}</h2><p>{copy}</p></div></article>)}</div><span className="scrub-instruction">{reducedMotion ? 'Movimento reduzido: leitura completa disponível' : journeyVideoSrc ? 'Role para atravessar a jornada' : 'Vídeo da jornada aguardando integração'}</span></div>
    </section>
    <section className="journey-after"><p className="section-kicker">O que acontece depois do percurso</p><h2>As perguntas organizam aquilo que ainda estava sem nome.</h2><div className="journey-after__grid"><p>A INÉDITA cruza sua história, capacidades, acessos, recursos, preferências e restrições.</p><p>As possibilidades são comparadas, questionadas e confrontadas com a realidade antes de uma recomendação aparecer.</p><p>O resultado explica por que aquela oportunidade faz sentido para você e quais caminhos podem seguir.</p></div><CTA href={startHref} tone="light">Descobrir minhas oportunidades</CTA></section>
    <PublicLadder tone="dark" />
    <section className="application-bridge"><div><p className="section-kicker">Depois da descoberta</p><h2>Se o próximo passo pedir companhia, existe uma aplicação.</h2><p>O acompanhamento mais próximo depende da análise do projeto e da disponibilidade.</p></div><CTA href={applyHref} tone="light">Aplicar para acompanhamento</CTA></section>
    <Footer variant="dark" />
  </main>;
}

function LabIndex() {
  const variants = [
    { href: '/biomorphic', label: '01 / Biomorphic', title: 'A investigação como objeto editorial.', copy: 'Paleta mineral, imagens BIOMORPHIC e uma leitura sobre o valor latente.', tone: 'bio' },
    { href: '/business', label: '02 / Empresas', title: 'Oportunidades para negócios que precisam decidir.', copy: 'Uma superfície empresarial para capacidades internas, ativos e problemas reais.', tone: 'business' },
    { href: '/journey', label: '03 / Jornada', title: 'A ideia aparece enquanto você atravessa.', copy: 'Uma jornada scroll scrub com vídeo de fundo e capítulos progressivos.', tone: 'journey' },
  ];
  return <main className="lab-index"><Header variant="dark" label="Subprojeto INEDITA" /><section className="lab-index__hero"><p className="section-kicker">inedita-landing-lab / três superfícies</p><h1>O mesmo método.<br /><em>Três formas de entrar.</em></h1><p>Variações para testar a primeira impressão sem perder a verdade do produto: descobrir, estruturar e decidir.</p></section><section className="variant-grid" aria-label="Variações disponíveis">{variants.map((variant) => <a className={`variant-tile variant-tile--${variant.tone}`} href={variant.href} key={variant.href}><span>{variant.label}</span><h2>{variant.title}</h2><p>{variant.copy}</p><b>Ver variação <Arrow /></b></a>)}</section><Footer variant="dark" /></main>;
}

function App() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const standaloneEntry = String(import.meta.env.VITE_INEDITA_ROOT_ENTRY || '').trim();
  const standalonePages = {
    biomorphic: <BiomorphicPage />,
    business: <BusinessPage />,
    journey: <JourneyPage />,
  };

  if (path === '/' && standalonePages[standaloneEntry]) return standalonePages[standaloneEntry];
  if (path === '/biomorphic') return <BiomorphicPage />;
  if (path === '/business') return <BusinessPage />;
  if (path === '/journey') return <JourneyPage />;
  return <LabIndex />;
}

createRoot(document.getElementById('root')).render(<App />);
